import React, { useState } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { PAYMENT_STATUSES } from '../../../shared/constants/paymentStatuses';
import { PROOF_ACCEPT, validateProofFile, uploadPaymentProof } from '../../../shared/utils/paymentProof';
import CollectorPayBlock from './CollectorPayBlock';
import ProofThumbnails from '../../../shared/components/ProofThumbnails';

const SUBMIT_METHODS = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'cash_app', label: 'Cash App' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

const MemberPaymentSubmitModal = ({ hand, existingPayment, memberData, collectorPayinfo, onClose, onSuccess }) => {
  const isEdit = existingPayment?.status === PAYMENT_STATUSES.MEMBER_SUBMITTED;
  const owed = Number(hand.contribution_amount || 0);

  const [form, setForm] = useState({
    amount: isEdit
      ? String(existingPayment.submitted_amount ?? hand.contribution_amount ?? '')
      : String(hand.contribution_amount || ''),
    method: isEdit ? (existingPayment.submitted_method || '') : '',
    reference: isEdit ? (existingPayment.submitted_reference || '') : '',
    notes: isEdit ? (existingPayment.submitted_notes || '') : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Optional proof — now MULTIPLE images (Stage 31: split-payment receipts). Image 1
  // goes to payments.proof_path (unchanged); the rest to payment_proofs child rows.
  const [proofFiles, setProofFiles] = useState([]);
  // Member affirmation before submit (a premature "I Paid" becomes deliberate).
  const [confirmed, setConfirmed] = useState(false);

  const onProofChange = (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const v = validateProofFile(file);
      if (v.error) { setError(v.error); e.target.value = ''; return; }
    }
    setError(null);
    // APPEND new selections (browsers don't merge across picks), dedup by name+size,
    // so a member can add more receipts across multiple picks and still remove any.
    setProofFiles((prev) => {
      const key = (f) => `${f.name}:${f.size}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...files.filter((f) => !seen.has(key(f)))];
    });
    // Reset the input so re-picking the same file re-fires onChange after a remove.
    e.target.value = '';
  };

  const removeProofFile = (idx) => setProofFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reference.trim()) {
      setError('Reference number is required');
      return;
    }
    if (!confirmed) {
      setError('Please confirm you paid the full amount and attached all your receipts.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      // Always resolve member_id (uuid) from the hands table so it is never null.
      const { data: handRecord } = await supabaseClient
        .from('hands').select('member_id').eq('id', hand.id).single();
      const memberId = handRecord?.member_id ?? null;

      // Resolve the round context. Edit reuses the existing payment's round; a fresh
      // submit finds the live round.
      let liveRound = null;
      let roundId = existingPayment?.round_id ?? null;
      if (!existingPayment) {
        const { data } = await supabaseClient
          .from('rounds')
          .select('id, cycle_number, round_number')
          .eq('group_id', memberData.group_id)
          .in('status', ['open', 'funded', 'paid_out'])
          .order('cycle_number', { ascending: false })
          .order('round_number', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (!data) throw new Error('No active round is open for contributions yet.');
        liveRound = data;
        roundId = data.id;
      }

      // Proof is OPTIONAL and now MULTIPLE. Image 1 -> proof_path (unchanged); images
      // 2..N -> payment_proofs child rows (after the payment id is known). An upload
      // failure surfaces inline and aborts, so nothing is silently dropped.
      let proofPath = existingPayment?.proof_path ?? null;
      const childPaths = [];
      if (proofFiles.length) {
        try {
          for (let i = 0; i < proofFiles.length; i++) {
            const p = await uploadPaymentProof({ file: proofFiles[i], groupId: memberData.group_id, roundId });
            if (i === 0) proofPath = p; else childPaths.push(p);
          }
        } catch (upErr) {
          console.error('Proof upload error:', upErr);
          setError('Could not upload a receipt image. Remove it to submit without, or try again.');
          setLoading(false);
          return;
        }
      }

      const payload = {
        status: PAYMENT_STATUSES.MEMBER_SUBMITTED,
        submitted_amount: parseFloat(form.amount),
        submitted_method: form.method,
        submitted_reference: form.reference.trim(),
        submitted_notes: form.notes.trim() || null,
        submitted_at: now,
        member_id: memberId,
        proof_path: proofPath,
      };

      let paymentId = existingPayment?.id ?? null;
      if (existingPayment) {
        const { error: updateErr } = await supabaseClient
          .from('payments').update(payload).eq('id', existingPayment.id);
        if (updateErr) throw updateErr;
      } else {
        const { data: inserted, error: insertErr } = await supabaseClient
          .from('payments')
          .insert({
            group_id: memberData.group_id,
            hand_id: hand.id,
            amount_expected: hand.contribution_amount,
            cycle_number: liveRound.cycle_number,
            round_number: liveRound.round_number,
            round_id: liveRound.id,
            ...payload,
          })
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        paymentId = inserted?.id ?? null;
      }

      // Link the additional receipts (images 2..N) to the payment. Non-fatal on
      // failure — the contribution + primary receipt are already saved.
      if (childPaths.length && paymentId != null) {
        const { error: ppErr } = await supabaseClient
          .from('payment_proofs')
          .insert(childPaths.map((p) => ({ payment_id: paymentId, path: p })));
        if (ppErr) console.error('payment_proofs insert error:', ppErr);
      }

      // The payment_submitted notification is now created SERVER-SIDE by the
      // notify_confirmer_on_submit trigger (Stage 44), which routes it to the confirmer
      // — the collector in direct groups, the admin in Model B — with enriched metadata.
      // (Previously this client always notified the admin, so a direct-group collector
      // got no proactive alert.) No client insert here → no duplicate.

      await supabaseClient.from('audit_logs').insert({
        event_category: 'financial',
        event_type: isEdit ? 'payment_submission_edited' : 'payment_submitted',
        actor_id: user.id,
        actor_role: 'member',
        old_value: existingPayment ? { status: existingPayment.status } : null,
        new_value: { status: PAYMENT_STATUSES.MEMBER_SUBMITTED, ...payload },
        risk_level: 'low',
        flagged: false,
        timestamp: now,
      });

      onSuccess();
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to submit payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {isEdit ? 'Edit Submission' : 'I Paid'} — Hand {hand.position_number}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
        </div>
        {/* Direct groups: where to send this payment (current collector's handles). */}
        <CollectorPayBlock payinfo={collectorPayinfo} className="mb-4" />

        {/* Amount-due guidance (COPY: Frannie to finalize). Splitting is fine, but the
            full amount must be paid and every receipt attached before submitting. */}
        <div className="mb-4 rounded-lg border border-warning-200 bg-warning-50 p-3">
          <p className="text-sm font-semibold text-warning-900">Amount due: ${owed.toFixed(2)}</p>
          <p className="text-xs text-warning-800 mt-1">
            Pay the full amount — you can split it into multiple transactions if the app limits you.
            Attach a receipt for each transaction, then mark it paid. Upload all your receipts before submitting.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
            <select
              value={form.method}
              onChange={e => setForm({ ...form, method: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base"
              required
            >
              <option value="">Select method</option>
              {SUBMIT_METHODS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reference Number(s) *</label>
            <input
              type="text"
              value={form.reference}
              onChange={e => setForm({ ...form, reference: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base"
              placeholder="If you split your payment into multiple transactions, enter all the reference numbers here (e.g. 12345, 67890)."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-base"
              rows={2}
              placeholder="Any additional details..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Payment screenshots <span className="text-neutral-400 font-normal">(optional — one per transaction)</span>
            </label>
            <input
              type="file"
              accept={PROOF_ACCEPT}
              multiple
              onChange={onProofChange}
              className="w-full text-sm text-neutral-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-neutral-200 file:text-neutral-700"
            />
            {proofFiles.length > 0 && (
              <ul className="mt-2 space-y-1">
                {proofFiles.map((f, i) => (
                  <li key={`${f.name}:${f.size}:${i}`} className="flex items-center justify-between gap-2 text-xs bg-success-50 border border-success-200 rounded px-2 py-1">
                    <span className="text-success-800 truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeProofFile(i)}
                      className="text-error-600 hover:text-error-800 font-medium shrink-0"
                      aria-label={`Remove ${f.name}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-neutral-500">JPG, PNG, or WebP, up to 5MB each. Kept private — only your group admin/collector can view them.</p>
            {/* Receipts already attached to this submission (edit). */}
            {isEdit && existingPayment && (
              <ProofThumbnails paymentId={existingPayment.id} primaryPath={existingPayment.proof_path} className="mt-2" />
            )}
          </div>

          {/* Member affirmation (COPY: Frannie to finalize). */}
          <label className="flex items-start gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>I confirm I've paid the full ${owed.toFixed(2)} and attached all my receipts.</span>
          </label>

          {error && <p className="text-error-600 text-sm">{error}</p>}
          <div className="flex flex-col space-y-2">
            <button
              type="submit"
              disabled={loading || !confirmed}
              className="w-full bg-primary-600 text-neutral-50 rounded-lg px-4 py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : isEdit ? 'Update Submission' : 'Submit Payment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full border border-neutral-300 rounded-lg px-4 py-3 text-base text-neutral-700"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberPaymentSubmitModal;
